import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import TaxCodeCard from "../components/taxCodes/TaxCodeCard";
import CreateTaxCodeModal from "../components/taxCodes/CreateTaxCodeModal";
import UpdateTaxCodeModal from "../components/taxCodes/UpdateTaxCodeModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import {
  getTaxCodes,
  deleteTaxCode,
  resetTaxCodeServerStatus,
} from "../redux/actions/taxCodeActions";
import PageError from "../components/common/PageError";

const TaxCodesPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    taxCodes,
    loadingTaxCodes,
    errorLoadingTaxCodes,
    serverStatus,
    loadingTaxCodeDelete,
    loadingTaxCodeUpdate,
    loadingTaxCodeCreate,
    errorCreatingTaxCode,
    errorUpdatingTaxCode,
  } = useSelector((state) => state.taxCode);

  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTaxCode, setSelectedTaxCode] = useState(null);

  const canCreate = hasPermission("tax_code:create");
  const canUpdate = hasPermission("tax_code:update");
  const canDelete = hasPermission("tax_code:delete");

  const fetchTaxCodes = useCallback(() => {
    dispatch(getTaxCodes());
  }, [dispatch]);

  useEffect(() => {
    fetchTaxCodes();
  }, [fetchTaxCodes]);

  const filteredTaxCodes = useMemo(() => {
    if (!taxCodes) return [];
    if (!searchTerm.trim()) return taxCodes;

    const query = searchTerm.toLowerCase();
    return taxCodes.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.code?.toLowerCase().includes(query),
    );
  }, [taxCodes, searchTerm]);

  const handleOpenCreate = () => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingTaxCodeCreate && setIsCreateModalOpen(false),
    [loadingTaxCodeCreate],
  );

  const handleOpenUpdate = (item) => {
    setSelectedTaxCode(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingTaxCodeUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTaxCode(null);
    }
  }, [loadingTaxCodeUpdate]);

  const handleOpenDelete = (item) => {
    setSelectedTaxCode(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingTaxCodeDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTaxCode(null);
    }
  }, [loadingTaxCodeDelete]);

  const handleDeleteConfirm = () => {
    if (selectedTaxCode) dispatch(deleteTaxCode(selectedTaxCode.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetTaxCodeServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = () => {
    if (loadingTaxCodes) return <AppCircularProgress />;
    if (errorLoadingTaxCodes)
      return <PageError error={errorLoadingTaxCodes} onClick={fetchTaxCodes} />;

    if (filteredTaxCodes.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography color="text.secondary">
            {searchTerm
              ? `No tax codes match "${searchTerm}"`
              : "No tax codes found."}
          </Typography>
        </Box>
      );

    return (
      <Grid container spacing={3}>
        {filteredTaxCodes.map((item) => (
          <Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
            <TaxCodeCard
              taxCode={item}
              onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
              onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Tax Codes"
      subtitle="Define value added tax rates and other tax categories."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Tax Code
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search tax codes..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateTaxCodeModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingTaxCodeCreate}
          error={errorCreatingTaxCode}
        />
      )}
      {isUpdateModalOpen && selectedTaxCode && (
        <UpdateTaxCodeModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          taxCode={selectedTaxCode}
          loading={loadingTaxCodeUpdate}
          error={errorUpdatingTaxCode}
        />
      )}
      {isDeleteModalOpen && selectedTaxCode && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Tax Code"
          message={`Delete "${selectedTaxCode.name}"?`}
          loading={loadingTaxCodeDelete}
        />
      )}
    </PageContainer>
  );
};

export default TaxCodesPage;
